import PropTypes from 'prop-types';
import { Icon, Button, ButtonTooltip } from '@evertrue/et-components';

const DomainWarning = ({ domainStatus, isOwner }) => {
  if (domainStatus === 'configured' || !domainStatus) return null;

  return (
    <div className="step-card--domain-warning">
      <Icon icon="warning" className="step-card--domain-warning-icon" />
      <span className="step-card--domain-warning-text">
        {domainStatus === 'not_configured' ? (
          <>
            A sending domain must be set up by an EverTrue Owner on the{' '}
            {isOwner ? (
              <a href="/settings/org_settings" className="step-card--domain-warning-link">
                Organization Settings page
              </a>
            ) : (
              'Organization Settings page'
            )}
            . (This is different from your Email Outreach Account.)
          </>
        ) : (
          'Your sending domain is pending DNS verification (up to 48 hours).'
        )}
      </span>
    </div>
  );
};

DomainWarning.propTypes = {
  domainStatus: PropTypes.string,
  isOwner: PropTypes.bool,
};

/**
 * Automated email step card for the cadence flow editor.
 * Email steps are always automated — no manual/automated toggle.
 * domainStatus: 'not_configured' | 'pending' | 'configured'
 */
const EmailStepCard = ({ step, stepNumber, onDelete, onEditEmail, domainStatus, isOwner }) => {
  const displayTitle = step.name || 'Automated Email';

  const renderContentState = () => {
    if (step.template) {
      return (
        <button
          type="button"
          className="step-card--content-state step-card--content-state-linked"
          onClick={onEditEmail}
        >
          Email Template: {step.template.name}
        </button>
      );
    }
    if (step.subjectOverride || step.emailBody) {
      return (
        <span className="step-card--content-state step-card--content-state-custom">
          No template — using custom copy
        </span>
      );
    }
    return (
      <button type="button" className="step-card--add-content" onClick={onEditEmail}>
        + Add email content
      </button>
    );
  };

  return (
    <div className="step-card step-card--email">
      <div className="step-card--header">
        <div className="step-card--title-row">
          <div className="step-card--icon-wrapper step-card--icon-email">
            <Icon icon="email" />
          </div>
          <div className="step-card--title-content">
            <span className="step-card--title">{displayTitle}</span>
            {step.description ? (
              <span className="step-card--content-state">{step.description}</span>
            ) : (
              renderContentState()
            )}
          </div>
        </div>
        <div className="step-card--header-actions" onClick={(e) => e.stopPropagation()}>
          <Button type="simple" onClick={onEditEmail} title="Edit email">
            <Icon icon="edit" />
          </Button>
          <Button type="simple" onClick={onDelete} title="Delete step">
            <Icon icon="delete" />
          </Button>
        </div>
      </div>
      <DomainWarning domainStatus={domainStatus} isOwner={isOwner} />
      <div className="step-card--footer">
        <div className="step-card--manual-badge">
          <Icon icon="email" />
          <span>Automated Email</span>
        </div>
        <ButtonTooltip
          contentWidth={280}
          tooltipPositionTop
          trigger={<span className="step-card--automation-help">?</span>}
        >
          <span>This message will be sent to constituents automatically as soon as they reach this step.</span>
        </ButtonTooltip>
      </div>
    </div>
  );
};

EmailStepCard.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    template: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    name: PropTypes.string,
    description: PropTypes.string,
    subject: PropTypes.string,
    subjectOverride: PropTypes.string,
    body: PropTypes.string,
    emailBody: PropTypes.string,
    waitDuration: PropTypes.number,
    waitUnit: PropTypes.string,
  }).isRequired,
  stepNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onDelete: PropTypes.func.isRequired,
  onEditEmail: PropTypes.func.isRequired,
  domainStatus: PropTypes.oneOf(['not_configured', 'pending', 'configured']),
  isOwner: PropTypes.bool,
};

export default EmailStepCard;
